import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Fragment, useContext, useState } from "react";
import ReactGA from "react-ga4";
import { Navigate, useNavigate } from "react-router-dom";

import SubscriptionModal, { SubscriptionModalType } from "../SubscriptionModal";

import UserContext from "context/UserContext";

import Card from "components/atoms/Card";
import Modal from "components/atoms/Modal";
import Spinner from "components/atoms/Spinner";
import SubscriptionCard from "components/molecules/SubscriptionCard";
import CheckoutForm from "components/organisms/CheckoutForm";

import { error, success } from "utils";
import {
  NUTRITION_PLATFORM_GROWTH_SLUG,
  NUTRITION_PLATFORM_PRO_SLUG,
  NUTRITION_PLATFORM_SLUG,
  NUTRITION_PLATFORM_STARTER_SLUG,
  uniqueEventId,
} from "utils/products";
import { CustomerStatusType } from "utils/types";

interface PlatformTierConfig {
  tier: "starter" | "growth" | "pro";
  slug: string;
  name: string;
  price: number;
  priceDisplay: string;
  tokensMillions: number;
  refillPrice: string;
  refillPricePerM: number;
  description: string;
  features: string[];
}

const PLATFORM_TIERS: PlatformTierConfig[] = [
  {
    tier: "starter",
    slug: NUTRITION_PLATFORM_STARTER_SLUG,
    name: "Starter",
    price: 99,
    priceDisplay: "$99",
    tokensMillions: 1,
    refillPrice: "$25 per 1M refill",
    refillPricePerM: 25,
    description: "For getting started with Nutrition AI",
    features: [
      "Easy AI + SDK access",
      "Localization support",
      "Email + Community Slack support",
    ],
  },
  {
    tier: "growth",
    slug: NUTRITION_PLATFORM_GROWTH_SLUG,
    name: "Growth",
    price: 599,
    priceDisplay: "$599",
    tokensMillions: 5,
    refillPrice: "$10 per 1M refill",
    refillPricePerM: 10,
    description: "For growing applications",
    features: [
      "Everything in Starter",
      "Model switching",
      "Insights dashboard",
      "Dedicated Slack support",
    ],
  },
  {
    tier: "pro",
    slug: NUTRITION_PLATFORM_PRO_SLUG,
    name: "Pro",
    price: 2999,
    priceDisplay: "$2,999",
    tokensMillions: 100,
    refillPrice: "$2.50 per 1M refill",
    refillPricePerM: 2.5,
    description: "For high-volume production",
    features: [
      "Everything in Growth",
      "AI Insights",
      "Enterprise support",
      "Lowest refill rate",
    ],
  },
];

const TIER_CHECKOUT_INFO: Record<
  string,
  { name: string; price: string; tokens: string; refillPrice: string }
> = {
  [NUTRITION_PLATFORM_STARTER_SLUG]: {
    name: "Starter",
    price: "$99/mo",
    tokens: "1M tokens included",
    refillPrice: "$25 per 1M refill",
  },
  [NUTRITION_PLATFORM_GROWTH_SLUG]: {
    name: "Growth",
    price: "$599/mo",
    tokens: "5M tokens included",
    refillPrice: "$10 per 1M refill",
  },
  [NUTRITION_PLATFORM_PRO_SLUG]: {
    name: "Pro",
    price: "$2,999/mo",
    tokens: "100M tokens included",
    refillPrice: "$2.50 per 1M refill",
  },
};

const GLOBAL_ROUTE_AVG_DAYS = 30;

type RouteAverageRow = {
  category: string;
  totalCalls: number;
  avgBilledTokens: number;
  totalBilledTokens: number;
};

type RawRouteAverageRow = {
  category?: string;
  totalCalls?: number | string;
  avgBilledTokens?: number | string;
  totalBilledTokens?: number | string;
  total_calls?: number | string;
  avg_billed_tokens?: number | string;
  total_billed_tokens?: number | string;
};

const toNumber = (v: unknown): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const normalizeRouteAverageRows = (raw: unknown): RouteAverageRow[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => row as RawRouteAverageRow)
    .map((row) => ({
      category: row.category ?? "",
      totalCalls: toNumber(row.totalCalls ?? row.total_calls),
      avgBilledTokens: toNumber(row.avgBilledTokens ?? row.avg_billed_tokens),
      totalBilledTokens: toNumber(
        row.totalBilledTokens ?? row.total_billed_tokens
      ),
    }))
    .filter((row) => row.category !== "" && row.totalCalls > 0);
};

const ManageSubscriptionPlatform = () => {
  const navigate = useNavigate();
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const productQuery = params.get("product") || "";

  const [unauthorized, setUnauthorized] = useState(false);
  const [customerStatus, setCustomerStatus] = useState<CustomerStatusType>({
    hasOrg: false,
    hasPaymentMethod: false,
  });
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<number | null>(null);

  const [unsubscribeModalIsOpen, setUnsubscribeModalIsOpen] = useState(false);
  const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false);
  const [pendingTier, setPendingTier] = useState<PlatformTierConfig | null>(
    null
  );

  const [subscriptionModal, setSubscriptionModal] =
    useState<SubscriptionModalType>({
      isOpen: false,
      planSlug: null,
      shouldUpdate: false,
    });

  const [globalRouteAverages, setGlobalRouteAverages] = useState<
    RouteAverageRow[]
  >([]);

  const userContext = useContext(UserContext);

  const { isLoading, isFetching, refetch } = useQuery(
    ["platformManageProducts"],
    () => axios.get("/accounts/products"),
    {
      enabled: !!userContext,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch products");
        if (err?.response?.status === 403) setUnauthorized(true);
      },
      onSuccess: (res: AxiosResponse) => {
        setCustomerStatus({
          hasOrg: res?.data?.hasOrg,
          hasPaymentMethod: res?.data?.hasPaymentMethod,
        });
        const dynamics = res?.data?.dynamicVolumeSubscriptions || [];
        const platformItem = dynamics.find(
          (item: any) => item?.info?.PackageName === "Nutrition Platform"
        );
        setIsSubscribed(platformItem?.isSubscribed ?? false);

        if (platformItem?.isSubscribed && platformItem?.stripeSubscription) {
          const sub = platformItem.stripeSubscription;
          if (sub.metadata?.tier) {
            setCurrentTier(sub.metadata.tier);
          }
          setCancelAtPeriodEnd(sub.cancel_at_period_end ?? false);
          if (sub.current_period_end) {
            setCurrentPeriodEnd(sub.current_period_end);
          }
        }
      },
    }
  );

  const { isLoading: routeAvgLoading, isFetching: routeAvgFetching } = useQuery(
    ["globalRouteAverages", GLOBAL_ROUTE_AVG_DAYS],
    () =>
      axios.get(
        `/accounts/products/nutrition-platform/usage/route-averages/global?days=${GLOBAL_ROUTE_AVG_DAYS}`
      ),
    {
      enabled: true,
      onSuccess: (res: AxiosResponse) =>
        setGlobalRouteAverages(normalizeRouteAverageRows(res?.data)),
    }
  );

  const changeTier = useMutation(
    async (newTier: string) => {
      return await axios.post(
        `/accounts/products/nutrition-platform/subscription/upgrade`,
        { newTier }
      );
    },
    {
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Something went wrong");
      },
      onSuccess: () => {
        success("Subscription tier changed successfully");
        setUpgradeConfirmOpen(false);
        setPendingTier(null);
        refetch();

        ReactGA.event({
          action: "platform_tier_changed",
          category: `${NUTRITION_PLATFORM_SLUG}_${currentTier}`,
          label: uniqueEventId(),
        });
      },
    }
  );

  const cancelSubscription = useMutation(
    async () =>
      await axios.post(
        `/accounts/products/nutrition-platform/subscription/cancel`
      ),
    {
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Something went wrong");
      },
      onSuccess: (res: AxiosResponse) => {
        success(
          "Subscription scheduled for cancellation at the end of your billing period"
        );
        setUnsubscribeModalIsOpen(false);
        setCancelAtPeriodEnd(true);
        if (res?.data?.cancelDate) {
          setCurrentPeriodEnd(res.data.cancelDate);
        }
        refetch();

        ReactGA.event({
          action: "platform_subscription_cancelled",
          category: `${NUTRITION_PLATFORM_SLUG}_${currentTier}`,
          label: uniqueEventId(),
        });
      },
    }
  );

  if (unauthorized) return <Navigate to="/access-restricted" />;

  if (isLoading || isFetching || isSubscribed === null) {
    return <Spinner />;
  }

  // ── Subscribed: show tier management page ──────────────────────────────
  if (isSubscribed) {
    const currentTierIndex = PLATFORM_TIERS.findIndex(
      (t) => t.tier === currentTier
    );

    const isCurrentTier = (idx: number) => idx === currentTierIndex;

    const buttonTextForTier = (idx: number): string => {
      if (idx === currentTierIndex) return "";
      if (idx > currentTierIndex) return "Upgrade";
      return "Downgrade";
    };

    const buttonOnClickForTier = (
      tierConfig: PlatformTierConfig,
      idx: number
    ) => {
      if (idx === currentTierIndex) return () => {};
      return () => {
        setPendingTier(tierConfig);
        setUpgradeConfirmOpen(true);
      };
    };

    const actionWord =
      pendingTier && currentTierIndex >= 0
        ? PLATFORM_TIERS.indexOf(pendingTier) > currentTierIndex
          ? "Upgrade"
          : "Downgrade"
        : "Change";

    return (
      <Fragment>
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
              Nutrition AI Platform &mdash; Manage Subscription
            </h2>
          </div>

          {changeTier.isLoading || cancelSubscription.isLoading ? (
            <Spinner />
          ) : (
            <>
              {/* Cancellation banner */}
              {cancelAtPeriodEnd && currentPeriodEnd && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                  <p className="text-amber-800 font-semibold">
                    Subscription cancelling on:{" "}
                    {new Date(currentPeriodEnd * 1000).toLocaleDateString(
                      undefined,
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    Your API key and Easy AI access will remain active until
                    this date. After that, your API key will become invalid and
                    no further charges will be made.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap place-content-evenly h-full">
                {PLATFORM_TIERS.map((tierConfig, index) => (
                  <SubscriptionCard
                    key={tierConfig.tier}
                    name={tierConfig.name}
                    price={
                      <>
                        <div className="text-gray-900 font-inter text-6xl font-extrabold leading-[60px]">
                          {tierConfig.priceDisplay}
                        </div>
                        <div className="text-gray-500 font-inter text-2xl font-medium leading-8">
                          /mo
                        </div>
                      </>
                    }
                    counterInput={<></>}
                    description={tierConfig.description}
                    descriptionAlt={tierConfig.refillPrice}
                    bulletsHtml={[
                      <>
                        <span className="font-extrabold">
                          {tierConfig.tokensMillions}M{" "}
                        </span>
                        <span>tokens per month included</span>
                      </>,
                    ]}
                    bullets={tierConfig.features}
                    isCurrent={isCurrentTier(index)}
                    buttonText={buttonTextForTier(index)}
                    buttonOnClick={buttonOnClickForTier(tierConfig, index)}
                    isCurrentButtonText="Product Details"
                    isCurrentButtonOnClick={() =>
                      navigate(`/details?product=${NUTRITION_PLATFORM_SLUG}`)
                    }
                    hideButton={cancelAtPeriodEnd && !isCurrentTier(index)}
                    showCancelButton={
                      isCurrentTier(index) && !cancelAtPeriodEnd
                    }
                    cancelButtonOnClick={() => setUnsubscribeModalIsOpen(true)}
                    disabled={
                      cancelAtPeriodEnd ||
                      isLoading ||
                      isFetching ||
                      changeTier.isLoading ||
                      cancelSubscription.isLoading
                    }
                    wide={false}
                  />
                ))}
              </div>

              {/* Feature comparison table */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Compare Plans</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Feature
                        </th>
                        {PLATFORM_TIERS.map((tier) => (
                          <th
                            key={tier.tier}
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {tier.name}
                            {tier.tier === currentTier && (
                              <span className="ml-1 text-indigo-600">
                                (current)
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Monthly Price
                        </td>
                        {PLATFORM_TIERS.map((tier) => (
                          <td
                            key={tier.tier}
                            className="px-6 py-4 text-sm text-gray-500 text-center"
                          >
                            {tier.priceDisplay}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Included Tokens
                        </td>
                        {PLATFORM_TIERS.map((tier) => (
                          <td
                            key={tier.tier}
                            className="px-6 py-4 text-sm text-gray-500 text-center"
                          >
                            {tier.tokensMillions}M
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Refill Price
                        </td>
                        {PLATFORM_TIERS.map((tier) => (
                          <td
                            key={tier.tier}
                            className="px-6 py-4 text-sm text-gray-500 text-center"
                          >
                            {tier.refillPrice}
                          </td>
                        ))}
                      </tr>
                      {[
                        ["Model Switching", false, true, true],
                        ["Insights", false, true, true],
                        ["AI Insights", false, false, true],
                      ].map(([feature, ...vals]) => (
                        <tr key={feature as string}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {feature as string}
                          </td>
                          {(vals as boolean[]).map((v, i) => (
                            <td
                              key={i}
                              className="px-6 py-4 text-sm text-center"
                            >
                              {v ? (
                                <span className="text-green-600">Yes</span>
                              ) : (
                                <span className="text-red-500">No</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Estimated cost per 1K calls by route type */}
              <Card className="sm:p-6 mt-8">
                <p className="font-semibold text-gray-900 mb-1">
                  Estimated Cost Per 1,000 Calls by Route Type
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {globalRouteAverages.length > 0
                    ? `Based on platform-wide average token usage over the last ${GLOBAL_ROUTE_AVG_DAYS} days. Costs shown use each tier\u2019s refill rate.`
                    : "Loading platform-wide average token usage\u2026"}
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-gray-500 text-xs uppercase tracking-wider">
                        <th className="py-2 pr-4">Route Type</th>
                        <th className="py-2 pr-4 text-right">
                          Avg Tokens / Call
                        </th>
                        {PLATFORM_TIERS.map((t) => (
                          <th key={t.tier} className="py-2 text-right">
                            {t.name}
                            <br />
                            <span className="text-[10px] normal-case font-normal">
                              ({t.refillPrice})
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {globalRouteAverages
                        .map((r) => ({
                          route: r.category,
                          avgTokens: Math.round(r.avgBilledTokens),
                        }))
                        .map((row) => (
                          <tr
                            key={row.route}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-2.5 pr-4 text-gray-900">
                              {row.route}
                            </td>
                            <td className="py-2.5 pr-4 text-right font-medium text-indigo-600">
                              {routeAvgLoading || routeAvgFetching
                                ? "..."
                                : `~${row.avgTokens.toLocaleString()}`}
                            </td>
                            {PLATFORM_TIERS.map((t) => (
                              <td
                                key={t.tier}
                                className="py-2.5 text-right text-gray-700"
                              >
                                {(
                                  (row.avgTokens * t.refillPricePerM * 1000) /
                                  1000000
                                ).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            ))}
                          </tr>
                        ))}
                      {!routeAvgLoading &&
                        !routeAvgFetching &&
                        globalRouteAverages.length === 0 && (
                          <tr>
                            <td
                              colSpan={2 + PLATFORM_TIERS.length}
                              className="py-4 text-center text-sm text-gray-500"
                            >
                              No route-average data available yet.
                            </td>
                          </tr>
                        )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300">
                        <td
                          colSpan={2 + PLATFORM_TIERS.length}
                          className="pt-3 text-xs text-gray-400"
                        >
                          Based on platform-wide usage from the last{" "}
                          {GLOBAL_ROUTE_AVG_DAYS} days. Costs reflect the refill
                          rate for each tier and do not include monthly included
                          tokens.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Tier change confirmation modal */}
        <Modal
          isOpen={upgradeConfirmOpen}
          setOpen={setUpgradeConfirmOpen}
          title={`${actionWord} to ${pendingTier?.name}?`}
          disabled={changeTier.isLoading}
          isLoading={changeTier.isLoading}
          description={`You will be ${actionWord.toLowerCase()}d to the ${
            pendingTier?.name
          } plan at ${pendingTier?.priceDisplay}/month with ${
            pendingTier?.tokensMillions
          }M tokens included. Your billing will be prorated.`}
          submitButtonName={`Confirm ${actionWord}`}
          buttonType="primary"
          onSubmitted={() => {
            if (pendingTier) {
              changeTier.mutate(pendingTier.tier);
            }
          }}
        />

        {/* Cancel subscription modal */}
        <Modal
          isOpen={unsubscribeModalIsOpen}
          setOpen={setUnsubscribeModalIsOpen}
          title="Cancel Subscription?"
          disabled={cancelSubscription.isLoading}
          isLoading={cancelSubscription.isLoading}
          description={
            "Are you sure you want to cancel your Nutrition AI Platform subscription? " +
            "Your API key will become invalid and your Easy AI access will end as soon as " +
            "your current billing cycle ends. You will not be charged again, but you can " +
            "continue using the service until then."
          }
          submitButtonName="Cancel Subscription"
          buttonType="warning"
          type="error"
          onSubmitted={() => cancelSubscription.mutate()}
        />
      </Fragment>
    );
  }

  // ── Not subscribed: checkout flow ──────────────────────────────────────

  const tierSlug = productQuery;
  const tierInfo = TIER_CHECKOUT_INFO[tierSlug];
  const tierName = tierSlug.replace("nutrition-platform-", "");

  // Base slug with no tier → redirect to detail page
  if (!tierInfo) {
    return <Navigate to={`/details?product=${NUTRITION_PLATFORM_SLUG}`} />;
  }

  function closeSubscriptionModal() {
    refetch();
    setSubscriptionModal({
      isOpen: false,
      planSlug: null,
      shouldUpdate: false,
    });
  }

  return (
    <Fragment>
      <div className="mt-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Subscribe to Nutrition AI Platform &mdash;{" "}
          <span className="text-indigo-600">{tierInfo.name}</span>
        </h2>
        <p className="text-gray-500 mb-6 text-sm">
          {tierInfo.price} &middot; {tierInfo.tokens} &middot;{" "}
          {tierInfo.refillPrice}
        </p>

        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2 sm:p-6 h-fit">
            <h3 className="font-semibold text-gray-900 mb-4">Plan Summary</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Plan</dt>
                <dd className="font-medium text-gray-900">{tierInfo.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Monthly Price</dt>
                <dd className="font-medium text-gray-900">{tierInfo.price}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Included Tokens</dt>
                <dd className="font-medium text-gray-900">{tierInfo.tokens}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Refill Rate</dt>
                <dd className="font-medium text-gray-900">
                  {tierInfo.refillPrice}
                </dd>
              </div>
            </dl>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-400">
                Includes Easy AI, SDK access, localization, and the full REST
                API. Auto-refill enabled by default.
              </p>
            </div>
          </Card>

          <CheckoutForm
            productPlanSlug={tierSlug}
            shouldCreateOrg={!customerStatus.hasOrg}
            shouldCapturePayment={!customerStatus.hasPaymentMethod}
            shouldUpdateSubscription={false}
            subscriptionData={{
              tier: tierName,
              refillBehaviour: "auto",
              refillBatchQuantity: 1,
            }}
          />
        </div>
      </div>

      <SubscriptionModal
        isOpen={subscriptionModal.isOpen}
        closeModal={closeSubscriptionModal}
        closeBtn={true}
        shouldCreateOrg={!customerStatus.hasOrg}
        shouldCapturePayment={!customerStatus.hasPaymentMethod}
        shouldUpdate={subscriptionModal.shouldUpdate}
        planSlug={subscriptionModal.planSlug || ""}
        subscriptionData={{}}
      />
    </Fragment>
  );
};

export default ManageSubscriptionPlatform;
